/**
 * Comment System (Sprint 3a) - delivers scheduled NPC reactions to player
 * comments and trims comment storage to the news items still in the feed
 * (the save only keeps comments for the most recent feedLimit news).
 */
import type { TickSystem } from '@app-games/core';
import type { GameState } from '../state.js';
import type { GameAction } from '../actions.js';
import type { InfoState } from '../infoState.js';

/** Drop comments/replies whose news left the feed (pure - exported for tests). */
export function trimComments(info: InfoState): InfoState {
  const liveIds = new Set(
    info.feed.filter(f => f.kind === 'news').map(f => f.instanceId)
  );
  const keep = (x: { instanceId: string }) => liveIds.has(x.instanceId);
  const playerComments = info.playerComments.filter(keep);
  const npcReplies = info.npcReplies.filter(keep);
  const pendingReplies = info.pendingReplies.filter(keep);
  if (
    playerComments.length === info.playerComments.length &&
    npcReplies.length === info.npcReplies.length &&
    pendingReplies.length === info.pendingReplies.length
  ) {
    return info;
  }
  return { ...info, playerComments, npcReplies, pendingReplies };
}

/** Move due pending replies into delivered npcReplies (pure - exported for tests). */
export function deliverDueReplies(info: InfoState, now: number): InfoState {
  if (!info.pendingReplies.some(r => now >= r.dueAt)) return info;
  const due = info.pendingReplies.filter(r => now >= r.dueAt);
  return {
    ...info,
    pendingReplies: info.pendingReplies.filter(r => now < r.dueAt),
    npcReplies: [
      ...info.npcReplies,
      ...due.map(r => ({
        instanceId: r.instanceId, nickIdx: r.nickIdx, replyIdx: r.replyIdx, mood: r.mood, at: now,
      })),
    ],
  };
}

export const commentSystem: TickSystem<GameState, GameAction> = {
  name: 'comments',

  update(state) {
    if (!state.isRunning || !state.info) return state;
    const info = trimComments(deliverDueReplies(state.info, Date.now()));
    return info === state.info ? state : { ...state, info };
  },
};
